import { DestroyRef, effect, inject, Injectable } from '@angular/core';

import { GeneralSettingsService } from '@services/helper/general-settings.service';
import { TokenService } from '@services/helper/token.service';

/** Activity that counts as "the user is still here". */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

/** Where the last-activity stamp is shared between tabs. */
const LAST_ACTIVITY_KEY = 'session-last-activity';

/**
 * Idle sign-out for the "Session Timeout" setting on Settings › General
 * (FEC-1160 QA: the page had nothing that actually did anything).
 *
 * Disabled unless the user picks a duration — `session_timeout_minutes` defaults
 * to `0`, so no existing session changes behaviour until someone opts in.
 *
 * Activity is stamped into `localStorage` rather than kept in memory so that
 * several open tabs share one idle clock: without that, a tab left in the
 * background would sign the user out while they were working in another.
 */
@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
  private readonly generalSettings = inject(GeneralSettingsService);
  private readonly tokenService = inject(TokenService);
  private readonly destroyRef = inject(DestroyRef);

  private timer: ReturnType<typeof setInterval> | null = null;
  private listening = false;

  private readonly onActivity = (): void => this.stampActivity();

  constructor() {
    effect(() => {
      const minutes = this.generalSettings.sessionTimeoutMinutes();
      const signedIn = !!this.tokenService.token();

      this.teardown();
      if (!signedIn || minutes <= 0) return;

      this.stampActivity();
      this.listen();

      /* Checked on an interval rather than one long timeout so that a machine
         waking from sleep is noticed promptly, and so another tab's activity
         (visible only through localStorage) extends this tab's deadline too. */
      this.timer = setInterval(() => this.checkIdle(minutes), 30_000);
    });

    this.destroyRef.onDestroy(() => this.teardown());
  }

  /** Records "now" as the most recent activity across every open tab. */
  private stampActivity(): void {
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    } catch {
      /* Private mode / storage disabled — the timer simply cannot arm. */
    }
  }

  /** Signs the user out once the configured idle window has elapsed. */
  private checkIdle(minutes: number): void {
    let last: number;
    try {
      last = Number(localStorage.getItem(LAST_ACTIVITY_KEY) ?? 0);
    } catch {
      return;
    }

    if (!Number.isFinite(last) || last <= 0) return;
    if (Date.now() - last < minutes * 60_000) return;

    this.teardown();
    /* `logout()` is idempotent and navigates to /auth/login, which is the
       user-visible signal — a toast would not survive the navigation. */
    this.tokenService.logout();
  }

  private listen(): void {
    if (this.listening) return;
    this.listening = true;
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, this.onActivity, { passive: true });
    }
  }

  private teardown(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.listening) {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, this.onActivity);
      }
      this.listening = false;
    }
  }
}
